import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SiteFooter } from "@/components/SiteFooter";
import { LayoutGrid, Store, Target, ChevronDown, BadgeCheck, Bell, LogOut, User, Layers, Users2, ShieldAlert, Inbox, Handshake, Megaphone, BookOpen, Info, HelpCircle, Mail, FileText, Shield, Home as HomeIcon, UserPlus, Menu, Contact, MessagesSquare, CalendarDays, Briefcase, Building2, Gauge, Search } from "lucide-react";
import { PC_MARK } from "@/lib/assets";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { BrandLogo } from "@/components/BrandLogo";
import { BrandNoticeBanner } from "@/components/BrandNoticeBanner";

const cap = (n) => (n > 99 ? "99+" : n);

export function Navbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout, config } = useAuth();
  const [unread, setUnread] = useState(0);
  const [newInquiries, setNewInquiries] = useState(0);
  const [collabRequests, setCollabRequests] = useState(0);
  const [connPending, setConnPending] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);

  const loadUnread = useCallback(() => {
    if (!user) return;   // never poll authenticated-only endpoints for guests (avoids 401→/login bounce)
    api.get("/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
  }, [user]);

  // Cheap, authoritative attention counts. NEVER call /dashboard/opportunities here
  // (that runs the Match Engine); use the light owner-scoped endpoints instead.
  const loadAttention = useCallback(() => {
    api.get("/me/listing-inquiries")
      .then((r) => setNewInquiries((r.data || []).filter((i) => i.status === "NEW").length))
      .catch(() => setNewInquiries(0));
    api.get("/stats")
      .then((r) => setCollabRequests(r.data.my_collaboration_requests || 0))
      .catch(() => setCollabRequests(0));
    api.get("/connections/summary")
      .then((r) => setConnPending(r.data.incoming_pending || 0))
      .catch(() => setConnPending(0));
    api.get("/messages/unread-count")
      .then((r) => setMsgUnread(r.data.unread || 0))
      .catch(() => setMsgUnread(0));
  }, []);

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 20000);
    return () => clearInterval(t);
  }, [loadUnread, pathname]);

  useEffect(() => {
    const at = (user && (user.account_type || "PROFESSIONAL")) || "";
    if (user && at === "PROFESSIONAL" && user.role !== "ADMIN") loadAttention();
    else { setNewInquiries(0); setCollabRequests(0); }
  }, [loadAttention, pathname, user]);

  if (!user) return null;

  const acctType = user.account_type || "PROFESSIONAL";
  const isReferralPartner = acctType === "REFERRAL_PARTNER";

  const oppAttention = newInquiries + collabRequests;
  const badgeFor = (key) => (key === "inquiries" ? newInquiries : key === "collaborations" ? collabRequests : 0);

  // Opportunities = pipeline/collaboration only. Personal management moved to the profile menu.
  const oppItems = [
    { to: "/opportunities", label: "My Opportunities", icon: Target },
    { to: "/leads", label: "Leads", icon: Contact },
    { to: "/inquiries", label: "Inquiries", icon: Inbox, badge: "inquiries" },
    { to: "/referrals", label: "Referrals", icon: Handshake },
    { to: "/collaborations", label: "Collaborations", icon: Handshake, badge: "collaborations" },
  ];

  const activeTop = (id) => {
    if (id === "dashboard") return pathname === "/";
    if (id === "marketplace") return pathname.startsWith("/marketplace") || pathname.startsWith("/listing");
    if (id === "buyer-needs") return pathname.startsWith("/buyer-need");
    if (id === "opportunities") return ["/opportunities", "/leads", "/referrals", "/inquiries", "/collaborations", "/collaboration"].some((p) => pathname.startsWith(p));
    if (id === "advertise") return pathname.startsWith("/advertise");
    if (id === "connections") return pathname.startsWith("/connections");
    return false;
  };

  const topCls = (active) => `flex items-center gap-2 whitespace-nowrap px-3 py-2 rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C79A3B] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F2C59] ${active ? "bg-[#C79A3B] text-[#0F2C59] font-semibold shadow-sm" : "text-white/85 hover:bg-white/10 hover:text-white"}`;

  const Badge = ({ n, testid }) => (n > 0 ? <span data-testid={testid} className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-white text-[#0F2C59] text-[10px] font-bold flex items-center justify-center">{cap(n)}</span> : null);

  return (
    <>
    <header data-testid="app-header" className="sticky top-0 z-50 bg-gradient-to-b from-[#143163] to-[#0F2C59] border-b border-[#C79A3B]/25 shadow-[0_2px_14px_rgba(15,44,89,0.35)]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 sm:h-[72px] flex items-center justify-between gap-1.5 sm:gap-3">
        <Link to="/" data-testid="logo-link" aria-label="PropertyConnect home" className="flex items-center gap-2.5 shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C79A3B]">
          <BrandLogo plate className="h-[58px] sm:h-[66px]" />
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {isReferralPartner ? (
            <>
              <Link to="/referrals" data-testid="nav-referrals" className={topCls(pathname.startsWith("/referrals"))}><Handshake className="h-4 w-4" />My Referrals</Link>
              <Link to="/marketplace" data-testid="nav-marketplace" className={topCls(activeTop("marketplace"))}><Store className="h-4 w-4" />Marketplace</Link>
            </>
          ) : (
          <>
          <Link to="/" data-testid="nav-dashboard" className={topCls(activeTop("dashboard"))}><LayoutGrid className="h-4 w-4" />Discover</Link>
          <Link to="/marketplace" data-testid="nav-marketplace" className={topCls(activeTop("marketplace"))}><Store className="h-4 w-4" />Marketplace</Link>
          <Link to="/buyer-needs" data-testid="nav-buyer-needs" className={topCls(activeTop("buyer-needs"))}><Search className="h-4 w-4" />Buyer Needs</Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Link to="/opportunities" data-testid="nav-opportunities" className={topCls(activeTop("opportunities"))}>
                <Target className="h-4 w-4" />Opportunities
                <Badge n={oppAttention} testid="nav-opportunities-badge" />
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </Link>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              {oppItems.map((it) => (
                <DropdownMenuItem key={it.to} onClick={() => navigate(it.to)} data-testid={`nav-opp-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="gap-2 cursor-pointer justify-between">
                  <span className="flex items-center gap-2"><it.icon className="h-4 w-4" /> {it.label}</span>
                  {it.badge && badgeFor(it.badge) > 0 && <span data-testid={`nav-opp-${it.badge}-badge`} className="min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center">{cap(badgeFor(it.badge))}</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/connections" data-testid="nav-connections" className={topCls(activeTop("connections"))}>
            <Users2 className="h-4 w-4" />Connections
            <Badge n={connPending} testid="nav-connections-badge" />
          </Link>
          </>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {acctType === "PROFESSIONAL" && user.role !== "ADMIN" && (
            <Link to="/messages" data-testid="nav-messages" className="relative h-10 w-10 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors">
              <MessagesSquare className="h-5 w-5 text-white" />
              {msgUnread > 0 && <span data-testid="messages-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center">{cap(msgUnread)}</span>}
            </Link>
          )}
          <Link to="/notifications" data-testid="notif-bell" className="relative h-10 w-10 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors">
            <Bell className="h-5 w-5 text-white" />
            {unread > 0 && <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center">{cap(unread)}</span>}
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger data-testid="profile-menu" className="flex items-center gap-2.5 rounded-full border border-white/20 bg-white/5 pl-1 pr-1.5 sm:pr-3 py-1 hover:bg-white/10 transition-colors">
              <img src={user.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
              <div className="hidden xl:block text-left leading-tight max-w-[180px]">
                <div className="text-[13px] font-semibold text-white flex items-center gap-1"><span className="truncate">{user.name}</span>{user.verification_status === "PROFESSIONAL_VERIFIED" && <BadgeCheck className="h-3.5 w-3.5 text-[#C79A3B] shrink-0" />}</div>
                <div className="text-[11px] text-white/60 truncate">{user.role_label}</div>
              </div>
              <ChevronDown className="hidden sm:block h-4 w-4 text-white/60 shrink-0" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">Personal workspace</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => navigate("/command-center")} data-testid="menu-command-center" className="gap-2 cursor-pointer"><Gauge className="h-4 w-4" /> My Command Center</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile" className="gap-2 cursor-pointer"><User className="h-4 w-4" /> My Profile</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/advertise")} data-testid="menu-advertise" className="gap-2 cursor-pointer"><Megaphone className="h-4 w-4" /> Promote / Advertise</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">Manage my content</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => navigate("/my-properties")} data-testid="menu-my-properties" className="gap-2 cursor-pointer"><Layers className="h-4 w-4" /> My Properties</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/my-buyers")} data-testid="menu-my-buyers" className="gap-2 cursor-pointer"><Users2 className="h-4 w-4" /> My Buyer Needs</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/my-events")} data-testid="menu-my-events" className="gap-2 cursor-pointer"><CalendarDays className="h-4 w-4" /> My Events</DropdownMenuItem>
              {config?.recruitment_enabled && <DropdownMenuItem onClick={() => navigate("/my-recruitment")} data-testid="menu-my-recruitment" className="gap-2 cursor-pointer"><Briefcase className="h-4 w-4" /> My Recruitment Posts</DropdownMenuItem>}
              {config?.organizations_enabled && <DropdownMenuItem onClick={() => navigate("/my-organizations")} data-testid="menu-my-organizations" className="gap-2 cursor-pointer"><Building2 className="h-4 w-4" /> My Organizations</DropdownMenuItem>}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">Account</DropdownMenuLabel>
              {user.role === "ADMIN" && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin" className="gap-2 cursor-pointer"><ShieldAlert className="h-4 w-4" /> Admin Control Center</DropdownMenuItem>}
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout" className="gap-2 cursor-pointer text-red-600"><LogOut className="h-4 w-4" /> Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mobile / tablet grouped navigation */}
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger data-testid="mobile-nav-trigger" className="xl:hidden h-10 w-10 rounded-full hover:bg-white/10 flex items-center justify-center"><Menu className="h-5 w-5 text-white" /></SheetTrigger>
            <SheetContent side="right" className="w-[min(86vw,360px)] max-w-full p-0 overflow-y-auto" data-testid="mobile-nav">
              <div className="p-4 space-y-1">
                {isReferralPartner ? (
                  <>
                    <MobileLink to="/referrals" icon={Handshake} label="My Referrals" onClick={() => setSheetOpen(false)} />
                    <MobileLink to="/marketplace" icon={Store} label="Marketplace" onClick={() => setSheetOpen(false)} />
                  </>
                ) : (
                <>
                <MobileLink to="/" icon={LayoutGrid} label="Discover" onClick={() => setSheetOpen(false)} />
                <MobileLink to="/marketplace" icon={Store} label="Marketplace" onClick={() => setSheetOpen(false)} />
                <MobileLink to="/buyer-needs" icon={Search} label="Buyer Needs" onClick={() => setSheetOpen(false)} />
                <MobileGroup label="Opportunities" icon={Target} badge={oppAttention} testid="mnav-opportunities" groups={[{ label: null, items: oppItems.map((i) => ({ ...i, count: i.badge ? badgeFor(i.badge) : 0 })) }]} onNavigate={() => setSheetOpen(false)} navigate={navigate} />
                <MobileLink to="/connections" icon={Users2} label="Connections" badge={connPending} onClick={() => setSheetOpen(false)} />
                </>
                )}
                <div className="h-px bg-slate-200 my-2" />
                <MobileLink to="/notifications" icon={Bell} label="Notifications" badge={unread} onClick={() => setSheetOpen(false)} />
                <div className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Personal workspace</div>
                <MobileLink to="/command-center" icon={Gauge} label="My Command Center" onClick={() => setSheetOpen(false)} />
                <MobileLink to="/profile" icon={User} label="My Profile" onClick={() => setSheetOpen(false)} />
                <MobileLink to="/advertise" icon={Megaphone} label="Promote / Advertise" onClick={() => setSheetOpen(false)} />
                <div className="h-px bg-slate-200 my-2" />
                <div className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Manage my content</div>
                <MobileLink to="/my-properties" icon={Layers} label="My Properties" onClick={() => setSheetOpen(false)} />
                <MobileLink to="/my-buyers" icon={Users2} label="My Buyer Needs" onClick={() => setSheetOpen(false)} />
                <MobileLink to="/my-events" icon={CalendarDays} label="My Events" onClick={() => setSheetOpen(false)} />
                {config?.recruitment_enabled && <MobileLink to="/my-recruitment" icon={Briefcase} label="My Recruitment Posts" onClick={() => setSheetOpen(false)} />}
                {config?.organizations_enabled && <MobileLink to="/my-organizations" icon={Building2} label="My Organizations" onClick={() => setSheetOpen(false)} />}
                <div className="h-px bg-slate-200 my-2" />
                <div className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Account</div>
                {user.role === "ADMIN" && <MobileLink to="/admin" icon={ShieldAlert} label="Admin Control Center" onClick={() => setSheetOpen(false)} />}
                <button onClick={async () => { setSheetOpen(false); await logout(); navigate("/login"); }} data-testid="mnav-sign-out" className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[15px] font-medium text-red-600 hover:bg-red-50"><LogOut className="h-4 w-4" /> Sign out</button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
    {user && <BrandNoticeBanner />}
    </>
  );
}

function MobileLink({ to, icon: Icon, label, badge = 0, onClick }) {
  return (
    <Link to={to} onClick={onClick} data-testid={`mnav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-[15px] font-medium text-slate-700 hover:bg-slate-100">
      <span className="flex items-center gap-2.5"><Icon className="h-4 w-4 text-slate-500" /> {label}</span>
      {badge > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center">{cap(badge)}</span>}
    </Link>
  );
}

function MobileGroup({ label, icon: Icon, groups, badge = 0, testid, navigate, onNavigate }) {
  const [open, setOpen] = useState(true);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger data-testid={`${testid}-trigger`} className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-[15px] font-semibold text-slate-800 hover:bg-slate-100">
        <span className="flex items-center gap-2.5"><Icon className="h-4 w-4 text-slate-500" /> {label}{badge > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center">{cap(badge)}</span>}</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-4" data-testid={`${testid}-content`}>
        {groups.map((group, gi) => (
          <div key={group.label || gi} className={gi > 0 ? "mt-1 pt-1 border-t border-slate-100" : ""}>
            {group.label && <div className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{group.label}</div>}
            {group.items.map((it) => (
              <button key={it.to} onClick={() => { navigate(it.to); onNavigate(); }} data-testid={`mnav-item-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-[14px] text-slate-600 hover:bg-slate-100">
                <span className="flex items-center gap-2.5"><it.icon className="h-4 w-4 text-slate-400" /> {it.label}</span>
                {it.count > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center">{cap(it.count)}</span>}
              </button>
            ))}
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function PageShell({ children, testid }) {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main data-testid={testid} className="flex-1 w-full mx-auto max-w-7xl px-4 sm:px-6 py-8">{children}</main>
      <SiteFooter variant="compact" />
    </div>
  );
}
